import { useEffect, useState } from 'react';
import type { ReactNode, VoidFunctionComponent } from 'react';

import { getCategoryDisplayName } from '../../../../../shared/issuesV2';
import { window } from '../../../../globals';
import { LabeledCheckbox } from '../../../generic/Forms';
import PropagateModal from '../../shared/SettingsPropagateModal';
import type { Category } from '../../../../../shared/issuesV2';

import usePropagateSettingsModal from './usePropagateSettingsModal';
import type { GenericIssueSettings, GenericIssueSettingsFields } from './utils';

type Props = {
  //  Issue category
  category: Category;
  //  If the modal is open or not
  isOpen: boolean;
  //  This is passed down from the useIssueSettings hook
  settings: GenericIssueSettings;
  //  Name of the selected policy. Could be undefined if no policy is selected
  policyName?: string;
  //  Name of the selected filter. Could be undefined if no filter is selected
  filterName?: string;
  //  Hide the modal
  onHide: () => void;
  //  Optionally render snippet settings
  renderSnippetSettings?: (props: {
    additionalFields: GenericIssueSettingsFields[];
    setAdditionalFields: (fields: GenericIssueSettingsFields[]) => void;
  }) => ReactNode;
};

/**
 * Records additional fields that can be propagated.
 *
 * Useful for tracking fields that vary across categories.
 * For example, snippet scanning applies only to the licensing category.
 */
const useAdditionalFields = (isOpen: boolean) => {
  const [additionalFields, setAdditionalFields] = useState<GenericIssueSettingsFields[]>([]);
  useEffect(() => {
    if (!isOpen) {
      setAdditionalFields([]);
    }
  }, [isOpen]);

  return { additionalFields, setAdditionalFields };
};

/**
 * SettingsOrganizationProjectIssuesPropagateModal is a generic for
 * the propagate settings modals. Despite the fact that all of the
 * different issue types have different settings, they can all essentially
 * be represented the same way.
 */
const SettingsOrganizationProjectIssuesPropagateModal: VoidFunctionComponent<Props> = ({
  category,
  isOpen,
  settings,
  policyName,
  filterName,
  onHide,
  renderSnippetSettings,
}) => {
  const { additionalFields, setAdditionalFields } = useAdditionalFields(isOpen);
  const displayCategory = getCategoryDisplayName(category);
  const {
    isScanningEnabled,
    toggleIsScanningEnabled,
    shouldUseSelectedPolicy,
    toggleShouldUseSelectedPolicy,
    isCICheckEnabled,
    toggleIsCICheckEnabled,
    shouldUseSelectedFilter,
    toggleShouldUseSelectedFilter,
    isSaveButtonDisabled,
    isLoading,
    errors,
    onSavePropagateSettings,
  } = usePropagateSettingsModal({
    orgId: window.user?.organizationId as number,
    category,
    isOpen,
    onHide,
  });
  return (
    <PropagateModal
      isOpen={isOpen}
      errors={errors}
      onHide={onHide}
      onSave={() => onSavePropagateSettings(additionalFields)}
      settingsCategory={displayCategory}
      isSaveButtonDisabled={isSaveButtonDisabled && additionalFields.length === 0}
      isSaveButtonLoading={isLoading}
    >
      <LabeledCheckbox
        label={settings.isScanningEnabled
          ? `Enable ${displayCategory} scanning for all projects`
          : `Disable ${displayCategory} scanning for all projects`}
        isChecked={isScanningEnabled}
        onChange={() => toggleIsScanningEnabled()}
        isRippleDisabled
      />
      <LabeledCheckbox
        label={policyName
          ? `Override all projects to use ${displayCategory} policy "${policyName}"`
          : `Unset the ${displayCategory} policy in all projects`}
        isChecked={shouldUseSelectedPolicy}
        onChange={() => toggleShouldUseSelectedPolicy()}
        isRippleDisabled
      />
      <LabeledCheckbox
        label={settings.isCICheckEnabled
          ? `Enable CI/CD ${displayCategory} checks for all projects`
          : `Disable CI/CD ${displayCategory} checks for all projects`}
        isChecked={isCICheckEnabled}
        onChange={() => toggleIsCICheckEnabled()}
        isRippleDisabled
      />
      <LabeledCheckbox
        label={filterName
          ? `Override all projects to use ${displayCategory} filter "${filterName}"`
          : `Unset the ${displayCategory} filter in all projects`}
        isChecked={shouldUseSelectedFilter}
        onChange={() => toggleShouldUseSelectedFilter()}
        isRippleDisabled
      />
      {renderSnippetSettings && renderSnippetSettings({ additionalFields, setAdditionalFields })}
    </PropagateModal>
  );
};

export default SettingsOrganizationProjectIssuesPropagateModal;
